import { useMutation, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { UserRound } from "lucide-react";
import { type FormEvent, useState } from "react";

import { api, ApiError } from "../api/client";
import type { LicenceStatus } from "../api/types";
import { attentionSelector, designate } from "../components/attention";
import { Callout } from "../components/Callout";
import { Dialog, DialogBody } from "../components/Dialog";
import { Presence } from "../components/presence";
import { Button, Field, inputClass } from "../components/ui";
import { k, t, tc } from "../i18n/i18n";
import { LicenceFrame } from "./ActivationScreen";
import { LICENCE_QUERY } from "./licence";

/** Qui utilise ce poste (administration des licences, demande d'Alexis du 02/11/2026) : nom et prénom obligatoires,
 *  grade et service facultatifs ; l'unité est celle de la licence, saisie par BELLICOM. Demandé une fois, après la
 *  notice de protection des données, puis modifiable dans Réglages › Ce poste. */

interface Draft {
  last_name: string;
  first_name: string;
  grade: string;
  service: string;
}

function draftOf(status: LicenceStatus): Draft {
  const identity = status.identity;
  return {
    last_name: identity?.last_name ?? "",
    first_name: identity?.first_name ?? "",
    grade: identity?.grade ?? "",
    service: identity?.service ?? "",
  };
}

/** Champ obligatoire encore vide, dans l'ordre du formulaire ; null quand tout est rempli. */
export function missingField(draft: Pick<Draft, "last_name" | "first_name">): "last_name" | "first_name" | null {
  if (!draft.last_name.trim()) return "last_name";
  if (!draft.first_name.trim()) return "first_name";
  return null;
}

/** Nom affiché d'une identité déclarée : « CNE Claire Martin · BOI / OPS ». */
export function identityLabel(status: LicenceStatus | undefined): string | null {
  const identity = status?.identity;
  if (!identity?.last_name || !identity.first_name) return null;
  const name = [identity.grade, identity.first_name, identity.last_name].filter(Boolean).join(" ");
  return identity.service ? `${name} · ${identity.service}` : name;
}

/** Formulaire partagé par l'écran bloquant et la fenêtre de Réglages. */
function IdentityForm({
  status,
  submitLabel,
  onSaved,
  onCancel,
  onDirty,
}: {
  status: LicenceStatus;
  submitLabel: string;
  onSaved?: () => void;
  onCancel?: () => void;
  onDirty?: (dirty: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const [initial] = useState(() => draftOf(status));
  const [draft, setDraft] = useState(initial);
  const missing = missingField(draft);
  const save = useMutation({
    mutationFn: () =>
      api.declareIdentity({
        last_name: draft.last_name,
        first_name: draft.first_name,
        grade: draft.grade || null,
        service: draft.service || null,
      }),
    onSuccess: (next) => {
      queryClient.setQueryData(LICENCE_QUERY, next);
      void queryClient.invalidateQueries({ predicate: (q) => q.queryKey[0] !== LICENCE_QUERY[0] });
      onSaved?.();
    },
  });
  const set = (key: keyof Draft) => (e: { target: { value: string } }) => {
    const next = { ...draft, [key]: e.target.value };
    setDraft(next);
    onDirty?.(JSON.stringify(next) !== JSON.stringify(initial));
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (missing) {
      designate(attentionSelector(`identity-${missing}`));
      return;
    }
    save.mutate();
  };
  return (
    <form onSubmit={submit} className="space-y-3">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label={tc("personne", "Nom")}>
          <input
            data-attention="identity-last_name"
            className={inputClass}
            value={draft.last_name}
            onChange={set("last_name")}
            maxLength={80}
            autoComplete="family-name"
            autoFocus={!initial.last_name}
          />
        </Field>
        <Field label={t("Prénom")}>
          <input
            data-attention="identity-first_name"
            className={inputClass}
            value={draft.first_name}
            onChange={set("first_name")}
            maxLength={80}
            autoComplete="given-name"
          />
        </Field>
        <Field label={t("Grade")} hint={t("Facultatif.")}>
          <input className={inputClass} value={draft.grade} onChange={set("grade")} maxLength={40} placeholder={t("Ex. : CNE")} />
        </Field>
        <Field label={t("Service")} hint={t("Facultatif.")}>
          <input className={inputClass} value={draft.service} onChange={set("service")} maxLength={80} placeholder={t("Ex. : BOI / OPS")} />
        </Field>
      </div>
      {status.licence_unit && (
        <p className="text-meta leading-snug text-ink-muted">{t("Unité de la licence : {unite}", { unite: status.licence_unit })}</p>
      )}
      <Presence show={Boolean(save.error)}>
        {save.error && (
          <Callout tone="danger">{save.error instanceof ApiError ? save.error.message : t("La déclaration n'a pas pu être enregistrée.")}</Callout>
        )}
      </Presence>
      <div className={clsx("flex gap-2", onCancel ? "justify-end" : "flex-col")}>
        {onCancel && (
          <Button variant="secondary" type="button" onClick={onCancel}>
            {t("Annuler")}
          </Button>
        )}
        {/* Nom ou prénom vide : le bouton reste cliquable et désigne le champ à remplir. */}
        <Button
          type="submit"
          className={onCancel ? undefined : "w-full"}
          disabled={save.isPending}
          blocked={Boolean(missing)}
          onBlocked={() => missing && designate(attentionSelector(`identity-${missing}`))}
        >
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}

const WHY = k(
  "BELLICOM rattache chaque poste à la personne qui l'utilise pour gérer les places de la licence et vous assister. Ni le contenu de vos dossiers ni vos saisies ne sont recueillis.",
);

/** Écran bloquant « Qui utilise ce poste ? », après la notice (une fois). `kept` : par-dessus l'application. */
export function IdentityScreen({ status, kept = false }: { status: LicenceStatus; kept?: boolean }) {
  return (
    <LicenceFrame kept={kept}>
      <h1 className="flex items-center gap-2 text-title font-semibold text-ink">
        <UserRound size={16} className="text-ink-muted" /> {t("Qui utilise ce poste ?")}
      </h1>
      <p className="mt-1.5 mb-4 text-ui leading-relaxed text-ink-soft">{t(WHY)}</p>
      <IdentityForm status={status} submitLabel={t("Enregistrer et continuer")} />
      <p className="mt-3 text-meta text-ink-muted">{t("Modifiable ensuite dans Réglages › Ce poste.")}</p>
    </LicenceFrame>
  );
}

/** Réglages › Ce poste : la personne déclarée, modifiable. */
export function IdentityDialog({ status, onClose }: { status: LicenceStatus; onClose: () => void }) {
  const [dirty, setDirty] = useState(false);
  return (
    <Dialog title={t("Ce poste")} subtitle={status.device_name ?? undefined} onClose={onClose} width={480} dirty={dirty}>
      <DialogBody>
        <p className="mb-4 text-ui leading-relaxed text-ink-soft">{t(WHY)}</p>
        <IdentityForm status={status} submitLabel={t("Enregistrer")} onSaved={onClose} onCancel={onClose} onDirty={setDirty} />
      </DialogBody>
    </Dialog>
  );
}
